import { useMemo, useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Apartment, Visibility, VisibilityOff } from '@mui/icons-material';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import * as admin from '@/api/admin';
import type { OrganizationSummary, OrganizationType } from '@/api/admin';
import { toApiError } from '@/api/client';
import { useImpersonation } from '@/auth/ImpersonationContext';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatDate } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/admin/organizaciones/')({
  component: OrganizacionesPage,
});

const ORG_TYPE: Record<OrganizationType, { label: string; color: 'default' | 'primary' | 'secondary' | 'info' }> = {
  None: { label: 'Sin productos', color: 'default' },
  Empresa: { label: 'Producto por clasificar', color: 'primary' },
  Estudio: { label: 'Estudio Contable', color: 'primary' },
  Integrador: { label: 'API para Programadores', color: 'info' },
  Hibrida: { label: 'Híbrida', color: 'secondary' },
};

function commercialProductFallback(product: string): string {
  if (product === 'Api') return 'API para Programadores';
  if (product === 'Estudio') return 'Estudio Contable';
  if (product === 'Empresa') return 'Producto por clasificar';
  return product;
}

function OrganizacionesPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [impersonating, setImpersonating] = useState<OrganizationSummary | null>(null);
  const navigate = useNavigate();
  const orgsQ = useQuery({ queryKey: ['organizations'], queryFn: () => admin.listOrganizations(0, 200) });

  const all = orgsQ.data?.items ?? [];
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((o) => o.name.toLowerCase().includes(q) || o.email.toLowerCase().includes(q));
  }, [all, search]);

  if (orgsQ.isLoading) return <LoadingScreen message="Cargando organizaciones…" />;

  return (
    <Box>
      <Stack sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Apartment color="primary" /> Organizaciones
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Todas las cuentas de la plataforma, con productos contratados y tipo de organización.
        </Typography>
      </Stack>

      <TextField size="small" placeholder="Buscar por nombre o email" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2, minWidth: 320 }} />

      {orgsQ.error ? (
        <MuiAlert severity="error">{toApiError(orgsQ.error).message}</MuiAlert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Organización</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Productos comerciales</TableCell>
                <TableCell>Suscripción</TableCell>
                <TableCell>Alta</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((o) => (
                <TableRow key={o.tenantId} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" color={ORG_TYPE[o.organizationType].color} label={ORG_TYPE[o.organizationType].label} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                      {(o.commercialProducts?.length ?? 0) > 0 ? (
                        o.commercialProducts!.map((p) => (
                          <Chip
                            key={`${p.technicalProduct}:${p.tier}`}
                            size="small"
                            variant="outlined"
                            label={`${p.productName} · ${p.tierName}`}
                          />
                        ))
                      ) : o.products.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      ) : (
                        o.products.map((p) => <Chip key={p} size="small" variant="outlined" label={commercialProductFallback(p)} />)
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell><Typography variant="body2">{o.subscriptionStatus}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{formatDate(o.createdAt)}</Typography></TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => navigate({ to: '/admin/tenants/$id', params: { id: o.tenantId } })}
                    >
                      Detalle
                    </Button>
                    <Button size="small" startIcon={<VisibilityOff />} onClick={() => setImpersonating(o)}>Impersonar</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {impersonating && <ImpersonateDialog org={impersonating} onClose={() => setImpersonating(null)} />}
    </Box>
  );
}

function ImpersonateDialog({ org, onClose }: { org: OrganizationSummary; onClose: () => void }): JSX.Element {
  const navigate = useNavigate();
  const { start } = useImpersonation();
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => admin.impersonate(org.tenantId, justification.trim()),
    onSuccess: (grant) => {
      start(grant, org.tenantId, org.name);
      void navigate({ to: '/dashboard' });
    },
    onError: (err) => setError(toApiError(err).message),
  });

  const valid = justification.trim().length >= 10;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Impersonar “{org.name}”</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 2 }}>
          Vas a iniciar una sesión <strong>de solo lectura</strong> sobre esta organización. La acción
          queda <strong>auditada</strong> con tu usuario y la justificación. Salí cuando termines.
        </DialogContentText>
        <TextField
          label="Justificación (obligatoria)"
          fullWidth
          multiline
          minRows={2}
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          placeholder="Ej.: soporte ticket #1234 — reproducir error de emisión"
          helperText={valid ? ' ' : 'Mínimo 10 caracteres.'}
          autoFocus
        />
        {error && <MuiAlert severity="error" sx={{ mt: 2 }}>{error}</MuiAlert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" color="warning" disabled={!valid || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? <CircularProgress size={18} /> : 'Impersonar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
